/**
 * Shared add-to-cart for forms outside the product page: the lists page (assets/gift-lists.js)
 * and the quickshop on collection cards (assets/card-quickshop.js). Posts a form to
 * /cart/add.js and announces the new line, so the cart bubble and the drawer update the same way
 * as after an add on the product page.
 * addWithinMax guards the maximum per order for every add on the page, the product page included,
 * and for quantity changes in the cart (assets/component-cart-items.js).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { eanOfControl, setEanOnFormData } from '@theme/ean-property';

const SHOP_ROOT = window.Shopify?.routes?.root ?? '/';

/**
 * Reads the cart as JSON.
 *
 * @returns {Promise<{ items?: { variant_id: number, quantity: number }[] }>}
 */
const readCart = async () => {
  const response = await fetch(`${SHOP_ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Cart read failed');
  return response.json();
};

/**
 * A refusal of the maximum per order (custom.max_order_quantity), or a cart that could not be
 * checked against it. Its message is meant for the customer, and nothing was posted.
 */
export class MaxQuantityError extends Error {}

/** The tail of every limited cart mutation on this page, see addWithinMax. */
let lastLimitedAdd = Promise.resolve();

/**
 * Runs `post` only when this product stays within its maximum per order, counted over every line
 * of the product (all variants, wrapped or not), like the old shop did. The cart check and the
 * post are one step: every limited mutation on the page (adds on the product page, quickshop cards
 * and lists, quantity changes in the drawer and on the cart page) waits for the previous one, so
 * two of them can never both pass the check on the same cart. A cart that cannot be read refuses
 * rather than skipping the check. Shopify has no maximum of its own (quantity rules are B2B only),
 * so other tabs and posts without JavaScript stay unchecked.
 *
 * @template T
 * @param {{ productId: number, maxQuantity: number, quantity: number, lineKey?: string }} limit -
 *   `maxQuantity` 0 means no limit: `post` then runs at once, outside the queue. Without `lineKey`
 *   `quantity` comes on top of the cart (an add); with it, `quantity` is the new quantity of that
 *   line (a change), and lowering a line is never refused.
 * @param {() => Promise<T>} post - The mutation itself; it runs inside the queue.
 * @returns {Promise<T>} What `post` returns; rejects with a MaxQuantityError when refused.
 */
export const addWithinMax = ({ productId, maxQuantity, quantity, lineKey }, post) => {
  if (!maxQuantity) return post();

  const run = lastLimitedAdd.then(async () => {
    let cart;
    try {
      cart = await readCart();
    } catch {
      throw new MaxQuantityError('We konden je winkelwagen niet controleren. Probeer het opnieuw.');
    }

    const lines = (cart.items ?? []).filter((item) => item.product_id === productId);
    const current = lineKey ? (lines.find((item) => item.key === lineKey)?.quantity ?? 0) : 0;
    const inCart = lines.reduce((sum, item) => sum + item.quantity, 0) - current;

    if (quantity > current && inCart + quantity > maxQuantity) {
      throw new MaxQuantityError(
        inCart >= maxQuantity
          ? `Maximaal ${maxQuantity} per bestelling: dit zit al in je winkelwagen.`
          : `Maximaal ${maxQuantity} per bestelling: je hebt er al ${inCart} in je winkelwagen.`
      );
    }

    return post();
  });

  // The next limited mutation starts once this one has settled, whatever its outcome.
  lastLimitedAdd = run.catch(() => {});
  return run;
};

/**
 * Posts one form to /cart/add.js and announces the new line.
 *
 * When the answer to the post is lost (network drop, a body that does not parse) the add may
 * still have gone through, and comparing cart quantities cannot settle that (the server may commit
 * after the check, other tabs change the cart). Such an uncertain outcome therefore always sends
 * the customer to /cart instead of inviting a second add.
 * A definite refusal from Shopify (sold out, all stock already in the cart) throws its own message.
 *
 * @param {HTMLFormElement} form
 * @param {{ submitter?: HTMLElement | null, source?: string }} [options] - `submitter` is the
 *   button that carries the variant id (name="id"), `source` names the caller in the event detail.
 * @returns {Promise<{ redirect: boolean }>}
 */
export const addToCart = async (form, { submitter = null, source = 'gift-lists' } = {}) => {
  const formData = new FormData(form);
  // Set by hand rather than through FormData's own submitter argument, which older Safari ignores.
  if (submitter instanceof HTMLButtonElement && submitter.name) formData.set(submitter.name, submitter.value);

  // The EAN property follows the variant actually added: the chip that was pressed, or the option
  // picked in the form's variant select. A form with neither keeps its own hidden properties[EAN].
  const eanControl = submitter?.dataset.ean != null ? submitter : form.querySelector('select[name="id"]');
  if (eanControl) setEanOnFormData(formData, eanOfControl(eanControl));

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => item.dataset.sectionId)
    .filter(Boolean);

  if (sectionIds.length > 0) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  // A form that carries data-max-quantity (custom.max_order_quantity) and data-product-id is
  // checked against the cart first; a refusal throws its message before anything is posted.
  const limit = {
    productId: Number(form.dataset.productId),
    maxQuantity: Number(form.dataset.maxQuantity) || 0,
    quantity: Number(formData.get('quantity')) || 1,
  };

  let response;
  let added;
  try {
    ({ response, added } = await addWithinMax(limit, async () => {
      const posted = await fetch(`${SHOP_ROOT}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      return { response: posted, added: await posted.json() };
    }));
  } catch (error) {
    if (error instanceof MaxQuantityError) throw error;
    // Lost or unreadable answer: the add may or may not have happened, so don't offer a retry.
    window.location.assign(`${SHOP_ROOT}cart`);
    return { redirect: true };
  }

  // Shopify answered and refused the line (sold out, or all stock already in the cart): the add
  // definitely did not happen, so the card shows the reason.
  if (added?.status) throw new Error(added.description || added.message || 'Dit product kan niet worden toegevoegd.');
  // Not ok and no refusal message: nothing to show the customer, and the outcome is unknown.
  if (!response.ok) {
    window.location.assign(`${SHOP_ROOT}cart`);
    return { redirect: true };
  }

  // The add itself succeeded: from here on the line is in the cart no matter what follows, so a
  // failing cart read must not re-enable a second add.
  try {
    const cart = await readCart();
    const deferred = CartLinesUpdateEvent.createPromise();

    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source, itemCount: 1, sections: added.sections, didError: false },
    });
    return { redirect: false };
  } catch {
    // Added, but the cart bubble/drawer can't be updated here: send the customer to /cart.
    window.location.assign(`${SHOP_ROOT}cart`);
    return { redirect: true };
  }
};
